/**
 * React Query key factory — catalog keys (CAT-001/003/004/007).
 *
 * Référence centrale dans `queryKeys.ts` (barrel).
 */

const catalogAll = ['catalog'] as const

export const catalogKeys = {
    catalog: {
        all: catalogAll,
        list: (entityId: string, filters?: object) =>
            [...catalogAll, 'list', entityId, filters] as const,
        detail: (id: string) =>
            [...catalogAll, 'detail', id] as const,
        // L'entité propriétaire d'une formation, lue SANS entité (périmètre de l'acteur) :
        // `useBasculeVersLEntiteDeLaFormation`. ⚠️ Une lecture PAR ARRIVÉE (`arrivee`) : une
        // propriétaire en cache, lue à une arrivée d'avant, peut avoir changé (transfert).
        proprietaire: (id: string, arrivee: string) =>
            [...catalogAll, 'proprietaire', id, arrivee] as const,
        nomenclatures: (productId: string, type: string) =>
            [...catalogAll, 'nomenclatures', productId, type] as const,
        references: (type: string, query: string) =>
            [...catalogAll, 'references', type, query] as const,
        pedagogicalTree: (productId: string) =>
            [...catalogAll, 'pedagogicalTree', productId] as const,
        expenseCategories: (entityId: string, search?: string) =>
            ['catalog', 'expenseCategories', entityId, search] as const,
        expenses: {
            all: ['catalog', 'expenses'] as const,
            list: (entityId: string, filters?: object) =>
                ['catalog', 'expenses', 'list', entityId, filters] as const,
            detail: (id: string) =>
                ['catalog', 'expenses', 'detail', id] as const,
            facets: (entityId: string) =>
                ['catalog', 'expenses', 'facets', entityId] as const,
            aggregations: (entityId: string) =>
                ['catalog', 'expenses', 'aggregations', entityId] as const,
        },
        formationExpenses: (productId: string) =>
            ['catalog', 'formationExpenses', productId] as const,
        articleCategories: (entityId: string, search?: string) =>
            ['catalog', 'articleCategories', entityId, search] as const,
        articles: {
            all: ['catalog', 'articles'] as const,
            list: (entityId: string, filters?: object) =>
                ['catalog', 'articles', 'list', entityId, filters] as const,
            detail: (id: string) =>
                ['catalog', 'articles', 'detail', id] as const,
            facets: (entityId: string) =>
                ['catalog', 'articles', 'facets', entityId] as const,
            aggregations: (entityId: string) =>
                ['catalog', 'articles', 'aggregations', entityId] as const,
            stockAlerts: (entityId: string) =>
                ['catalog', 'articles', 'stockAlerts', entityId] as const,
        },
        formationArticles: (productId: string) =>
            ['catalog', 'formationArticles', productId] as const,
        // RSC-012 §5.5 — le bloc « Matériel nécessaire » de l'onglet Général.
        // ⚠ La forme est IMPOSÉE par ses deux voisines ci-dessus : écrite
        //   `catalogKeys.formationEquipment(...)`, l'expression vaut `undefined`
        //   et `useQuery` lève. La couverture a sa propre clé — elle interroge
        //   le PARC, pas la fiche, et s'invalide donc à d'autres moments.
        formationEquipment: (productId: string) =>
            ['catalog', 'formationEquipment', productId] as const,
        formationEquipmentCoverage: (productId: string, entityId: string) =>
            ['catalog', 'formationEquipmentCoverage', productId, entityId] as const,
        // Le COÛT a aussi la sienne : il dépend des tarifs du prestataire, qui
        // changent hors de cette fiche. La partager avec la liste ferait
        // recharger l'un à chaque modification de l'autre, pour rien.
        formationEquipmentCost: (productId: string, entityId: string) =>
            ['catalog', 'formationEquipmentCost', productId, entityId] as const,
        // CAT-003: Pricing Engine
        pricingLabels: (entityId: string) =>
            ['catalog', 'pricingLabels', entityId] as const,
        priceMatrix: (productId: string) =>
            ['catalog', 'priceMatrix', productId] as const,
        commercialDefaults: (productId: string) =>
            ['catalog', 'commercialDefaults', productId] as const,
        // CAT-014: prix plancher (interne, gaté BUSINESS + catalog:view_floor_price)
        floorPrice: (productId: string) =>
            ['catalog', 'floorPrice', productId] as const,
        // CAT-007: Product Documents
        productDocuments: (productId: string) =>
            ['catalog', 'productDocuments', productId] as const,
        documentFolders: (productId: string) =>
            ['catalog', 'documentFolders', productId] as const,
        productProgram: (productId: string) =>
            ['catalog', 'productProgram', productId] as const,
        documentLogs: (documentId: string) =>
            ['catalog', 'documentLogs', documentId] as const,
        // CAT-004: Catalogue Argalis & Import/Export
        argalisFamilies: () => ['catalog', 'argalisFamilies'] as const,
        argalisProducts: (filters?: object) =>
            ['catalog', 'argalisProducts', filters] as const,
        argalisProductDetail: (id: string) =>
            ['catalog', 'argalisProduct', id] as const,
        catalogStatus: (productId: string) =>
            ['catalog', 'catalogStatus', productId] as const,
        // INSTR-004 §14 (dette CAT-001) : intervenants capables d'encadrer une
        // formation — sens inverse de `instructors.capabilities`, même table.
        capableInstructors: (productId: string) =>
            ['catalog', 'capableInstructors', productId] as const,
        // DOC-006 §5.5 — polling d'un job de génération en masse (fiches programmes).
        generationJob: (jobId: string) =>
            ['catalog', 'generationJob', jobId] as const,
    },
}
